import { PageContainer, PageHeader, Spread, Text, VStack } from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import {
  useClrPendingConfirmations,
  type ClrPendingConfirmationRow,
} from '@/hooks/queries/credit/usePendingConfirmations'
import { TooltipProvider } from '@repo/ui/components'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import {
  PendingConfirmationsTable,
  type PendingConfirmationRow,
} from '../-components/PendingConfirmationsTable'

export const Route = createFileRoute(
  '/_auth/_modules/credit/credit-limit-reviews/pending-confirmations/',
)({
  component: RouteComponent,
})

function toRow(row: ClrPendingConfirmationRow): PendingConfirmationRow {
  return {
    key: `clr:${row.reviewId}:${row.erp}:${row.creditSegment}`,
    erp: row.erp,
    businessName: row.businessName,
    creditSegment: row.creditSegment,
    reviewLabel: `#${row.reviewId}`,
    reviewTo: {
      to: '/credit/credit-limit-reviews/$reviewId',
      params: { reviewId: String(row.reviewId) },
    },
    approvedAt: row.approvedAt,
    approvedCreditLimit: row.approvedCreditLimit,
    currentCreditLimit: row.currentCreditLimit,
    approvedRiskClass: row.approvedRiskClass,
    currentRiskClass: row.currentRiskClass,
    approvedNextReviewDate: row.approvedNextReviewDate,
    currentNextReviewDate: row.currentNextReviewDate,
    segmentUpdatedAt: row.segmentUpdatedAt,
    dispatch: row.dispatch,
    status: row.isMismatch ? 'mismatch' : 'pending',
  }
}

/**
 * Approved single reviews whose values Fuze wrote onto the credit segment and the daily SAP
 * extract has not yet been seen holding. Live: a row leaves the list when the extract confirms
 * it, and turns red when the extract overwrote the approved values instead.
 */
function RouteComponent() {
  const { t } = useTranslation(['credit', 'common'])
  const query = useClrPendingConfirmations()
  const rows = (query.data?.results ?? []).map(toRow)

  return (
    <PageContainer maxWidth="wide">
      <Spread align="end">
        <VStack gap="xs">
          <PageHeader>{t('clr.pendingConfirmations.pageHeader')}</PageHeader>
          <Text tone="muted" className="max-w-3xl text-pretty">
            {t('clr.pendingConfirmations.pageDescription')}
          </Text>
        </VStack>
      </Spread>

      <Card>
        <CardContent className="pt-6">
          <TooltipProvider delayDuration={150}>
            <PendingConfirmationsTable
              rows={rows}
              isPending={query.isPending}
              error={query.error}
            />
          </TooltipProvider>
        </CardContent>
      </Card>
    </PageContainer>
  )
}
